import React, { useState, useContext, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  StyledPaymentScreen,
  StyledForm,
  StyledSummary,
  ButtonWrapperPositioner,
} from "./styles";
import {
  Button,
  Input,
  CountrySelect,
  StateSelect,
  RadioGroup,
  Radio,
  LoaderComponent,
  FlexDiv,
  Divider,
} from "../../components";
import { statesByCountry } from "../../components/StateSelect";
import { PaymentFormErrors } from "./types";

import { BookingQuoteContext } from "../../context/BookingContextProvider";
import useRentalGuardianProducts from "../../hooks/useRentalGuardianProducts";
import useCreateReservation from "../../hooks/useCreateReservation";
import assets from "../../assets";

const USA_COUNTRY_CODE = "330";
const TRAVEL_INSURANCE_NOT_SELECTED = "none";

const PaymentScreen = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loaderContent, setLoaderContent] = useState(
    "Starting your booking..."
  );

  const queryParams = new URLSearchParams(location.search);
  const adults = parseInt(queryParams.get("adults") || "0", 10);
  const children = parseInt(queryParams.get("children") || "0", 10);
  const coupon = queryParams.get("coupon") || "";

  const { unitData, quoteData, fromDate, toDate, rentalGuardianEnabled } =
    location.state || {};

  const bookingQuoteContext = useContext(BookingQuoteContext);
  if (!bookingQuoteContext) {
    throw new Error("BookingQuoteContext must be used within a Provider");
  }

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    country: { label: "United States", value: USA_COUNTRY_CODE },
    city: "",
    state: "",
    postalCode: "",
    travelInsurance: TRAVEL_INSURANCE_NOT_SELECTED,
  });

  const [errors, setErrors] = useState<PaymentFormErrors>({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  const selectedCountry = formData.country?.value;
  const selectedState = formData.state;

  const { data: travelInsuranceOptions, loading: loadingProducts } =
    useRentalGuardianProducts(
      id ?? "",
      {
        fromDate,
        toDate,
        guestCountry: String(formData.country.value),
        guestState: selectedCountry === "330" ? selectedState : "",
        quoteAmount: quoteData?.rentAmount,
      },
      rentalGuardianEnabled && selectedCountry === "330" && !!selectedState
    );

  const { createReservation, loading: creatingReservation } =
    useCreateReservation(id || "");

  useEffect(() => {
    if (quoteData) {
      setFormData((prev) => ({
        ...prev,
        firstName: quoteData.firstName || "",
        lastName: quoteData.lastName || "",
        email: quoteData.eMail || "",
        phone: quoteData.phoneNumber || "",
        address: quoteData.address || "",
        city: quoteData.city || "",
        state: quoteData.state || "",
        postalCode: quoteData.postalCode || "",
        country: { label: "United States", value: USA_COUNTRY_CODE },
        travelInsurance:
          quoteData.travelInsuranceProduct?.toString() ||
          TRAVEL_INSURANCE_NOT_SELECTED,
      }));
    }
  }, [quoteData]);

  console.log(unitData);

  // Validation Function
  const validatePhoneNumber = (phone: string) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/; // E.164 format
    return phoneRegex.test(phone);
  };

  const validateForm = (): boolean => {
    const newErrors: PaymentFormErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required.";
    if (!formData.lastName.trim())
      newErrors.lastName = "Last name is required.";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Valid email is required.";
    }
    if (!formData.phone.trim() || !validatePhoneNumber(formData.phone)) {
      newErrors.phone = "❗ Valid phone number is required.";
    }
    if (!formData.address.trim()) newErrors.address = "Address is required.";
    if (!formData.country || !formData.country.value) {
      newErrors.country = "Country is required.";
    }
    if (!formData.city.trim()) newErrors.city = "City is required.";
    if (formData.country.value === USA_COUNTRY_CODE && !formData.state.trim()) {
      newErrors.state = "State is required for the selected country.";
    }
    if (!formData.postalCode.trim())
      newErrors.postalCode = "Postal code is required.";

    setErrors(newErrors);

    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  // Trigger validation on input change
  useEffect(() => {
    validateForm();
  });

  const handleChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (
    name: string,
    selectedOption: { label: string; value: string } | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      country: selectedOption
        ? { label: selectedOption.label, value: String(selectedOption.value) }
        : { label: "", value: "" },
      state: "", // Reset state when country changes
      travelInsurance: TRAVEL_INSURANCE_NOT_SELECTED, // Reset travel insurance
    }));
  };

  const handleStateChange = (
    selectedState: { label: string; value: string } | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      state: selectedState ? selectedState.value : "",
      travelInsurance: TRAVEL_INSURANCE_NOT_SELECTED,
    }));
  };

  const handleTravelInsuranceChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      travelInsurance: value,
    }));
  };

  const handlePhoneChange = (phone: string) => {
    setFormData((prev) => ({ ...prev, phone }));
  };

  const handleSubmit = async () => {
    setFormSubmitted(true); // Mark the form as submitted
    const isValid = validateForm(); // Validate the form

    if (!isValid) {
      alert("❗ Please fill all required fields.");
      return;
    }

    const paymentIntentAmount = parseFloat(quoteData.firstPaymentAmount || "0");

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      travelInsuranceProduct:
        formData.travelInsurance === TRAVEL_INSURANCE_NOT_SELECTED
          ? undefined
          : formData.travelInsurance,
      fromDate: fromDate || "",
      toDate: toDate || "",
      adults,
      children,
      coupon,
      withPets: quoteData?.withPets || false,
      paymentIntentAmount: paymentIntentAmount.toFixed(2),
      paymentSuccessUrl: `${window.location.origin}/reservation?step=3&uid=${id}&email=${formData.email}`,
      paymentCancelUrl: `${window.location.origin}/payment-canceled?id=${id}`,
      paymentProvider: unitData?.building.paymentProvider || "stripe",
    };

    console.log("Payload being sent to createReservation:", payload); // Log the payload
    console.log(
      "Payment Intent Amount before submit:",
      payload.paymentIntentAmount
    ); // Log paymentIntentAmount

    try {
      const { sessionUrl } = await createReservation(payload);
      console.log("Session URL received:", sessionUrl); // Log the session URL
      window.location.href = sessionUrl; // Redirect to Stripe payment page
    } catch (error) {
      console.error("Error during reservation creation:", error); // Log the error
      alert("Failed to create reservation. Please try again.");
    }
  };

  const placeholderImage =
    theme === "dark"
      ? assets.imagePlaceholderDark
      : assets.imagePlaceholderWhite;

  useEffect(() => {
    if (creatingReservation) {
      const messages = [
        "Booking your reservation...",
        "Redirecting to payment...",
        "Finalizing your booking...",
      ];

      let index = 0;

      // Change the message every 2 seconds
      const interval = setInterval(() => {
        setLoaderContent(messages[index]);
        index = (index + 1) % messages.length;
      }, 2000);

      return () => clearInterval(interval); // Clean up on unmount or when creatingReservation changes
    }
  }, [creatingReservation]);

  if (creatingReservation) {
    return <LoaderComponent content={loaderContent} />;
  }

  return (
    <StyledPaymentScreen>
      <StyledForm
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <FlexDiv
          alignitems="center"
          gap="10px"
          style={{ margin: "0px 0px 20px 0px" }}
        >
          <img
            style={{ cursor: "pointer", filter: "invert(1)" }}
            onClick={() => navigate(-1)}
            height={20}
            src={assets.GoBack}
            alt="go back icon"
            className="go-back"
          />{" "}
          <h2 style={{ margin: "0px" }}>Payment Information</h2>
        </FlexDiv>

        <Input
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          placeholder="Enter first name"
          label="First Name"
          error={formSubmitted && errors.firstName ? errors.firstName : ""}
        />
        <Input
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          label="Last Name"
          placeholder="Enter last name"
          error={formSubmitted && errors.lastName ? errors.lastName : ""}
        />
        <Input
          name="email"
          value={formData.email}
          onChange={handleChange}
          label="E-mail"
          placeholder="Enter your email address"
          error={formSubmitted && errors.email ? errors.email : ""}
        />
        {/*<Input
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          label="Phone Number"
          placeholder="Enter your phone number"
        />*/}
        <label style={{}}>Phone Number</label>
        <ReactPhoneInput
          enableSearch
          searchClass="search-bar"
          searchPlaceholder="Search country ..."
          country={"us"}
          inputClass="phone-input"
          containerClass="container-phone"
          inputProps={{
            name: "phone",
            required: true,
          }}
          value={formData.phone}
          onChange={handlePhoneChange}
          placeholder="Enter your phone number"
        />
        {formSubmitted && errors.phone && (
          <div className="error-message">{errors.phone}</div>
        )}
        <div style={{ height: 16 }}></div>
        <Input
          name="address"
          value={formData.address}
          onChange={handleChange}
          label="Address"
          placeholder="Enter your address"
          error={formSubmitted && errors.address ? errors.address : ""}
        />

        <CountrySelect
          name="country"
          value={formData.country}
          onChange={handleCountryChange}
        />
        {formSubmitted && errors.country && (
          <div className="error-message">{errors.country}</div>
        )}
        <div style={{ height: 16 }}></div>

        {formData.country.value === USA_COUNTRY_CODE && (
          <>
            <StateSelect
              countryCode={formData.country.value}
              value={
                statesByCountry[formData.country.value]?.find(
                  (s: { value: string }) => s.value === formData.state
                ) || null
              }
              onChange={handleStateChange}
            />
            {formSubmitted && errors.state && (
              <div className="error-message">{errors.state}</div>
            )}
            <div style={{ height: 16 }}></div>
          </>
        )}
        <Input
          name="city"
          value={formData.city}
          onChange={handleChange}
          label="City"
          placeholder="Enter city"
          error={formSubmitted && errors.city ? errors.city : ""}
        />
        <Input
          name="postalCode"
          value={formData.postalCode}
          onChange={handleChange}
          label="Postal Code"
          placeholder="Enter postal code"
          error={formSubmitted && errors.postalCode ? errors.postalCode : ""}
        />

        {rentalGuardianEnabled && selectedCountry === USA_COUNTRY_CODE && (
          <>
            <h2 style={{ marginTop: 40 }}>Add Travel Insurance</h2>
            <p style={{ fontSize: 14 }}>
              Please select your state in order to see available travel
              insurance options.
            </p>
            {loadingProducts ? (
              <>
                <div style={{ height: "20px" }}></div>
                <LoaderComponent
                  isRelative
                  content="Loading available travel insurances ..."
                />
                <div style={{ height: "20px" }}></div>
              </>
            ) : (
              <RadioGroup
                name="travelInsurance"
                value={formData.travelInsurance}
                onChange={handleTravelInsuranceChange}
              >
                {travelInsuranceOptions?.length === 0 ? (
                  <p>No travel insurance options available.</p>
                ) : (
                  travelInsuranceOptions?.map((product, index) => (
                    <Radio
                      key={index}
                      value={product.product.id.toString()}
                      label={product.product.productCodeDescription}
                      price={
                        unitData.ratePlanInfo.currency.symbol +
                        product.quotePrice
                      }
                      description={product.descriptionShort}
                    ></Radio>
                  ))
                )}
                <Radio
                  value={TRAVEL_INSURANCE_NOT_SELECTED}
                  label="   I don't want travel insurance"
                ></Radio>
              </RadioGroup>
            )}
          </>
        )}
        <ButtonWrapperPositioner>
          <Button type="submit">Proceed to Payment</Button>
        </ButtonWrapperPositioner>
      </StyledForm>
      <StyledSummary>
        {unitData?.mainImage.image.urls.big && (
          <img
            src={unitData?.mainImage.image.urls.big || placeholderImage}
            onError={(e) => {
              (e.target as HTMLImageElement).src = placeholderImage;
            }}
            alt="Unit"
            style={{
              width: "100%",
              height: "auto",
              marginBottom: "16px",
              borderRadius: "8px",
            }}
          />
        )}

        {/* Trip Dates */}
        <p>
          <strong>Date of Trip:</strong> {fromDate || "-"} to {toDate || "-"}
        </p>

        <p>
          <strong>Guests:</strong> {adults} {adults === 1 ? "adult" : "adults"}
          {children > 0 &&
            `, ${children} ${children === 1 ? "child" : "children"}`}
        </p>

        {/* Rental Amount */}
        <p>
          <strong>Rental Amount:</strong> ${quoteData?.rentAmount || "0.00"}
        </p>

        {/* Fees */}
        {quoteData?.fees?.length > 0 && (
          <>
            <p>
              <strong>Fees:</strong>
            </p>
            <div>
              {quoteData.fees.map((fee: any, index: any) => (
                <div key={index} style={{ fontSize: 12 }}>
                  {fee.name}: ${fee.amount}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Security Deposits */}
        {quoteData?.securityDeposits?.length > 0 && (
          <>
            <p>
              <strong>Security Deposits:</strong>
            </p>
            <div>
              {quoteData.securityDeposits.map((deposit: any, index: any) => (
                <span key={index}>
                  {deposit.name}: ${deposit.amount}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Rental Guardian Price */}
        {formData.travelInsurance !== TRAVEL_INSURANCE_NOT_SELECTED && (
          <>
            <p>
              <strong>Rental Guardian:</strong> $
              {travelInsuranceOptions?.find(
                (option) =>
                  option.product.id.toString() === formData.travelInsurance
              )?.quotePrice || "0.00"}
            </p>
          </>
        )}
        <Divider />
        {/* Total Cost */}
        <h3>
          <strong>Total Cost:</strong> $
          {(
            parseFloat(quoteData?.rentAmount || "0") +
            (quoteData?.fees?.reduce(
              (acc: any, fee: any) => acc + parseFloat(fee.amount || "0"),
              0
            ) || 0) +
            (quoteData?.securityDeposits?.reduce(
              (acc: any, deposit: any) =>
                acc + parseFloat(deposit.amount || "0"),
              0
            ) || 0) +
            (formData.travelInsurance !== TRAVEL_INSURANCE_NOT_SELECTED
              ? parseFloat(
                travelInsuranceOptions?.find(
                  (option) =>
                    option.product.id.toString() === formData.travelInsurance
                )?.quotePrice || "0"
              )
              : 0)
          ).toFixed(2)}
        </h3>
      </StyledSummary>
    </StyledPaymentScreen>
  );
};

export default PaymentScreen;
